import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobSnippets = ({ jobs = [] }) => {
  const [selectedJob, setSelectedJob] = useState(0);

  // Show message if no jobs available
  if (!jobs || jobs.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-card-foreground mb-2">Real Job Opportunities</h3>
          <p className="text-sm text-muted-foreground mb-4">Live jobs from LinkedIn</p>
          <div className="text-center py-12">
            <Icon name="Briefcase" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h4 className="text-md font-semibold text-foreground mb-2">No matching jobs found</h4>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              We're constantly updating our job database. Check back soon or try analyzing for a different role.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentJob = jobs[selectedJob];

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Real Job Opportunities</h3>
            <p className="text-sm text-muted-foreground">Live jobs from LinkedIn</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedJob(Math.max(0, selectedJob - 1))}
              disabled={selectedJob === 0}
              className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="text-sm text-muted-foreground px-2">
              {selectedJob + 1} of {jobs.length}
            </span>
            <button
              onClick={() => setSelectedJob(Math.min(jobs.length - 1, selectedJob + 1))}
              disabled={selectedJob === jobs.length - 1}
              className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Job Header */}
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-primary/10 flex-shrink-0 flex items-center justify-center">
              <Icon name="Briefcase" size={24} className="text-primary" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h4 className="text-lg font-semibold text-foreground">{currentJob.job_title}</h4>
                  <p className="text-sm text-muted-foreground">{currentJob.company_name}</p>
                </div>
                <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                  currentJob.match_percentage >= 75 
                    ? 'bg-success/10 text-success' 
                    : currentJob.match_percentage >= 60 
                      ? 'bg-warning/10 text-warning' 
                      : 'bg-error/10 text-error'
                }`}>
                  <Icon name="Target" size={14} />
                  <span>{currentJob.match_percentage}% Match</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={14} />
                  <span>{currentJob.location || 'India'}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Briefcase" size={14} />
                  <span>{currentJob.experience_required || 'Not specified'}</span>
                </div>
                {currentJob.salary_estimate?.min && (
                  <div className="flex items-center space-x-1">
                    <Icon name="IndianRupee" size={14} />
                    <span>
                      ₹{(currentJob.salary_estimate.min / 100000).toFixed(1)}-
                      {(currentJob.salary_estimate.max / 100000).toFixed(1)}L
                    </span>
                  </div>
                )}
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span className="capitalize">{currentJob.job_type || 'full-time'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h5 className="font-medium text-foreground mb-3">Job Description</h5>
            <div className="text-sm text-muted-foreground leading-relaxed max-h-48 overflow-y-auto">
              {currentJob.job_description || 'No description available'}
            </div>
          </div>

          {/* Skills Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Matched Skills */}
            <div className="space-y-3">
              <h5 className="font-medium text-foreground flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span>Your Matching Skills</span>
              </h5>
              <div className="space-y-2">
                {currentJob.matching_skills && currentJob.matching_skills.length > 0 ? (
                  currentJob.matching_skills.slice(0, 8).map((skill, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-success rounded-full" />
                      <span className="text-foreground capitalize">{skill}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No matching skills identified</p>
                )}
              </div>
            </div>

            {/* Missing Skills */}
            <div className="space-y-3">
              <h5 className="font-medium text-foreground flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-warning" />
                <span>Skills to Develop</span>
              </h5>
              <div className="space-y-2">
                {currentJob.missing_skills && currentJob.missing_skills.length > 0 ? (
                  currentJob.missing_skills.slice(0, 8).map((skill, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-warning rounded-full" />
                      <span className="text-foreground capitalize">{skill}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No additional skills required</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            {currentJob.job_url && (
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="right"
                className="flex-1"
                onClick={() => window.open(currentJob.job_url, '_blank')}
              >
                View Full Job
              </Button>
            )}
            <Button
              variant="default"
              iconName="BookOpen"
              iconPosition="left"
              className="flex-1"
            >
              Get Learning Path
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSnippets;